//Used for Recent tab
import React, { Fragment, memo } from "react";
import {
  List,
  ListItem,
  Divider,
  Link,
  Body1,
  Caption1,
  makeStyles,
  mergeClasses,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import {
  Call20Filled,
  EditFilled,
  CallInboundRegular,
  CallOutboundRegular,
  CallMissedRegular,
} from "@fluentui/react-icons";

import { useCallHistoryContext } from "../../context/CallHistoryContext";
import { screenPop } from "../../utils/acs.salesforce";

const useStyles = makeStyles({
  noCallMsg: {
    margin: "1rem",
    color: tokens.colorNeutralForeground4,
  },
  listRoot: {
    width: "auto",
    padding: 0,
  },
  listItem: {
    padding: "0.45rem 0.85rem",
    borderBottom: "1px solid var(--neutralStroke2, #e1e1e1)",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground4,
    },
  },

  listItemGrid: {
    display: "grid",
    gridTemplateColumns: "80% 10% 10%",
    gap: 12,
    //alignItems: "center", //for icons to be in top row
  },

  // -------- Column 1 --------
  col1: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  iconLarge: {
    fontSize: "32px",
    lineHeight: "1",
  },

  // -------- Column 2 --------
  textDecorWithUnderline: {
    textDecoration: "underline",
    textDecorationColor: "currentColor",
  },
  boldText: {
    fontWeight: "bold",
  },
  required: {
    color: tokens.colorStatusDangerForeground1,
  },
  col2: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    overflow: "hidden",
  },

  col2Row1: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  bodyForUnknownRecord: {
    display: "inline-flex", // text + icon inline together
    alignItems: "center",
    gap: "6px", // space between text and icon
    whiteSpace: "normal", // allow wrapping
    wordBreak: "break-word", // break long words if needed
  },
  callIcon: {
    color: "var(--neutralForegroundDisabled, #8a8886)",
    cursor: "pointer",
    width: "16px",
    lineHeight: "1",
    flexShrink: 0,
  },
  nameLink: {
    color: "inherit",
    textDecoration: "none",
    display: "inline-block",
  },

  col2Row2: {
    fontSize: "0.85rem",
  },

  // -------- Column 3 --------
  col3: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "flex-start",
    gap: 4,
  },

  col3Row1: {
    fontSize: "18px",
    lineHeight: "1",
    display: "flex",
    color: "var(--neutralForeground3, #605E5C)",
  },
  queueLine: {
    color: "var(--neutralForegroundDisabled, #8a8886)",
  },

  //Show Edit as pencil icon
  col3Row2: {
    fontSize: "18px",
    color: "var(--neutralForeground3, #605E5C)",
    textDecoration: "none",
    cursor: "pointer",
    ":hover": {
      textDecoration: "none",
      color: "var(--neutralForeground3, #605E5C)",
    },
  },
});

function HistoryList({ onPlaceCall }) {
  const styles = useStyles();
  const { history } = useCallHistoryContext(); //consume context

  if (!history || history?.length === 0) {
    return (
      <div className={mergeClasses(styles.listRoot, styles.noCallMsg)}>No recent calls...</div>
    );
  }

  const handleStartCall = (num, clickToDialObj) => {
    if (num) {
      onPlaceCall(num, clickToDialObj);
    }
  };

  return (
    <div className={styles.listRoot}>
      <List>
        {history.map((item, index) => {
          let phoneNumber = item?.callType === "Inbound" ? item?.callerNumber : item?.dialedNumber;

          let isInternal = typeof phoneNumber === "string" && phoneNumber.includes("@");
          let isTransferLeg = false;
          if (item.callType === "Inbound" && item?.callerUpn && item?.name) {
            isTransferLeg = true;
          } else if (item.callType === "Outbound" && item?.dialedUpn && item?.name) {
            isTransferLeg = true;
          }

          let recordName = item.whoId
            ? item.whoName || ""
            : item.whatId
              ? item.whatName || ""
              : isTransferLeg || isInternal
                ? item.name
                : "Unknown Record";

          const recordType = item.whoId
            ? item.whoType || ""
            : item.whatId
              ? item.whatType || ""
              : "";

          const recordId = item.whoId || item.whatId || "";

          const isUnknownRecord =
            (!item?.whoId || item?.whoId === "") && (!item?.whatId || item?.whatId === "");

          if (!phoneNumber && isTransferLeg) {
            if (item?.callType === "Inbound") {
              phoneNumber = item.callerUpn || "";
            } else {
              phoneNumber = item.dialedUpn || "";
            }
          }
          const numForUnknownRecord =
            isUnknownRecord && !isTransferLeg && !isInternal ? phoneNumber : "";

          const bodyClasses =
            item.ansDurInSec === 0
              ? mergeClasses(styles.boldText, styles.textDecorWithUnderline, styles.required)
              : mergeClasses(styles.boldText, styles.textDecorWithUnderline);

          let IconComponentForCallType;
          let labelForCallType;
          let classNameForCallType;

          if (item.callType === "Inbound" && item.ansDurInSec === 0) {
            IconComponentForCallType = CallMissedRegular;
            labelForCallType = "Missed incoming";
            classNameForCallType = styles.required;
          } else if (item.callType === "Inbound" && item.ansDurInSec > 0) {
            IconComponentForCallType = CallInboundRegular;
            labelForCallType = "Incoming";
          } else if (item.callType !== "Inbound") {
            IconComponentForCallType = CallOutboundRegular;
            labelForCallType = "Outgoing";
            if (item.ansDurInSec === 0) {
              classNameForCallType = styles.required;
            }
          }

          let callHandler = null;
          let clickToDialObj = {};
          if (phoneNumber?.includes("@") && item?.callerGraphUserId) {
            clickToDialObj.params = "graphUserId=" + item.callerGraphUserId;
          }

          clickToDialObj.number = phoneNumber;
          clickToDialObj.objectType = recordType ? recordType : ""; //todo future
          if (isUnknownRecord && !isTransferLeg) {
            clickToDialObj.recordId = "";
            clickToDialObj.recordName = "";
          } else {
            clickToDialObj.recordId = recordId || "";
            clickToDialObj.recordName =
              recordName && recordName != "Unknown Record" ? recordName : "";
          }
          callHandler = () => handleStartCall(phoneNumber, clickToDialObj);

          return (
            <Fragment key={item.id}>
              <ListItem className={styles.listItem}>
                <div className={styles.listItemGrid}>
                  {/* -------- Column 1 -------- */}
                  {/*<div className={styles.col1}>
                  {item.whoId ? (
                      <ContactCardRegular className={styles.iconLarge}/>
                  ) : item.whatId ? (
                      <BuildingRegular />
                  ) : (
                      <PersonQuestionMarkRegular />
                  )}
                  </div>*/}

                  {/* -------- Column 2 -------- */}
                  <div className={styles.col2}>
                    <div className={styles.col2Row1}>
                      {/*<Body1 className={mergeClasses(styles.nameLink,bodyClasses,styles.bodyForUnknownRecord)}>
                          {numForUnknownRecord} {"  "}
                          <Call20Filled className={styles.callIcon} 
                            onClick={() => handleStartCall(numForUnknownRecord)}/>
                        </Body1>*/}
                      {numForUnknownRecord ? (
                        <Body1 className={mergeClasses(styles.nameLink, bodyClasses)}>
                          {numForUnknownRecord}
                        </Body1>
                      ) : recordId ? (
                        <Link
                          href="#"
                          role="button"
                          onClick={() => {
                            screenPop({
                              type: "SOBJECT",
                              params: { recordId },
                            });
                          }}
                          className={styles.nameLink}
                          aria-label={`Open details for ${recordName}`}
                          title={recordName}
                        >
                          <Body1 className={bodyClasses}>{recordName}</Body1>
                        </Link>
                      ) : (
                        <Body1 className={mergeClasses(styles.nameLink, bodyClasses)}>
                          {recordName}
                        </Body1>
                      )}
                    </div>

                    <div className={styles.col2Row2}>
                      <Body1 className={classNameForCallType}>
                        {IconComponentForCallType && (
                          <IconComponentForCallType className={classNameForCallType} />
                        )}{" "}
                        {labelForCallType}
                      </Body1>
                    </div>

                    <div className={styles.col2Row2}>
                      <Caption1 className={item.ansDurInSec === 0 ? styles.required : undefined}>
                        {item.startedAtDateTimeStr}
                      </Caption1>
                    </div>

                    {/*replaced Body1 by caption1 to decrease font */}
                    {item.queueName && (
                      <div className={styles.col2Row3}>
                        <Caption1
                          className={
                            item.ansDurInSec === 0
                              ? mergeClasses(styles.col2Row1, styles.required)
                              : styles.col2Row1
                          }
                        >
                          Queue :{" "}
                        </Caption1>
                        <Caption1 className={item.ansDurInSec === 0 ? styles.required : undefined}>
                          {item.queueName}
                        </Caption1>
                      </div>
                    )}

                    {item.transferredBy && (
                      <div className={styles.col2Row3}>
                        <Caption1
                          className={
                            item.ansDurInSec === 0
                              ? mergeClasses(styles.col2Row1, styles.required)
                              : styles.col2Row1
                          }
                        >
                          Transferred By :{" "}
                        </Caption1>
                        <Caption1 className={item.ansDurInSec === 0 ? styles.required : undefined}>
                          {item.transferredBy}
                        </Caption1>
                      </div>
                    )}
                  </div>

                  {/* -------- Column 3 -------- */}
                  {/*<div className={styles.col3}>
                    <div className={styles.col3Row1}>
                      {numForUnknownRecord && (
                        <Tooltip content="Call" relationship="label"> 
                          <Call20Filled className={classNameForCallType} onClick={() => handleStartCall(numForUnknownRecord)}/>
                        </Tooltip>
                      )}
                    </div>
                  </div>*/}
                  <div className={styles.col3}>
                    <div className={styles.col3Row1}>
                      {phoneNumber && (
                        <Tooltip content="Call" relationship="label">
                          <Call20Filled className={classNameForCallType} onClick={callHandler} />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  {/* -------- Column 4 -------- */}
                  <div className={styles.col3}>
                    <div className={styles.col3Row1}>
                      <Tooltip content={`Edit ${item.subject}`} relationship="label">
                        <EditFilled
                          className={classNameForCallType}
                          onClick={() => {
                            screenPop({
                              type: "URL",
                              params: {
                                url: `/lightning/r/Task/${item.activityId}/edit`,
                              },
                            });
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </ListItem>

              {index < history.length - 1 && <Divider appearance="subtle" />}
            </Fragment>
          );
        })}
      </List>
    </div>
  );
}

export default memo(HistoryList);
